/**
 * @fileoverview Physics core for the Hydraulic Balancing Testing simulation
 *
 * Pure functions only. The central upgrade over the original simulation is the
 * COUPLED hydraulic network: one circulation pump feeds a vertical riser with
 * a floor tee per storey; each room is a parallel branch. All resistances are
 * quadratic (Δp = r·V̇²), which allows an exact closed-form network reduction:
 *
 *   - branch conductance        c = 1/√r            (V̇ = c·√Δp)
 *   - parallel branches         c_par = Σ c_i
 *   - series resistance         r_ser = Σ r_i  →  c_ser = 1/√(Σ 1/c_i²)
 *
 * The network is reduced from the far end to the pump, intersected with the
 * pump curve, and flows are then propagated forward to every branch. Closing
 * one valve therefore genuinely pushes flow into all other rooms — the core
 * phenomenon of hydraulic balancing.
 *
 * Scientific basis:
 *   - Darcy-Weisbach pipe friction (constant f for transparency)
 *   - Valve Kv flow coefficient (V̇[m³/h] = Kv·√Δp[bar])
 *   - EN 442 radiator output Q = K·ΔT_m^1.3 with energy-balance iteration
 *   - DIN EN 12831-style room heat load at design conditions
 *   - Carnot-with-efficiency heat pump COP
 *
 * @module HydraulicBalancingTesting/physics
 */

import {
  WATER,
  AIR,
  BUILDING,
  AREAS,
  ROOM_VOLUME,
  ROOM_THERMAL_CAPACITANCE,
  PRESET_POSITIONS,
  TRV_P_BAND,
  RADIATOR,
  PIPES,
  PUMP,
  HEAT_PUMP,
  OUTDOOR,
  SCORING,
  createDefaultPipes,
} from './constants';

// #region GENERAL UTILITIES
// ============================================================================

export const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export const formatNumber = (v, decimals = 1) => {
  if (typeof v !== 'number' || !isFinite(v)) return '—';
  return v.toFixed(decimals);
};

/** m³/s → l/h */
export const toLitersPerHour = (q) => q * 3.6e6;

/** m³/s → m³/h */
export const toM3PerHour = (q) => q * 3600;

// #endregion

// #region HYDRAULIC RESISTANCES
// ============================================================================

/**
 * Quadratic resistance of a pipe run (supply + return) [Pa/(m³/s)²]
 * Δp = f·(L/D)·ρv²/2 with v = Q/A  →  r = f·(L/D)·ρ/(2A²)
 * @param {number} oneWayLength - One-way pipe length [m]
 * @param {number} diameter - Inner diameter [m]
 */
export const pipeResistance = (oneWayLength, diameter) => {
  const length = 2 * oneWayLength * PIPES.FITTING_FACTOR; // supply + return + fittings
  const area = Math.PI * diameter * diameter / 4;
  return PIPES.FRICTION_FACTOR * (length / diameter) * WATER.DENSITY / (2 * area * area);
};

/**
 * Quadratic resistance of a valve from its Kv value [Pa/(m³/s)²]
 * Kv definition: V̇[m³/h] = Kv·√(Δp[bar]) → Δp[Pa] = 1e5·(3600·Q/Kv)²
 * @param {number} kv - Flow coefficient [m³/h at 1 bar]
 */
export const kvResistance = (kv) => {
  const k = Math.max(kv, 1e-4);
  return 1e5 * (3600 / k) * (3600 / k);
};

/**
 * Resolves a (possibly partial) per-segment pipe-geometry override against the
 * full default network, so every physics function defaults to the catalogue
 * values and partial UI edits merge cleanly.
 * @returns {{base, risers:[], floors:[][], branches:[]}}
 */
export const resolvePipes = (override) => {
  const def = createDefaultPipes();
  if (!override) return def;
  return {
    base: { ...def.base, ...(override.base || {}) },
    risers: def.risers.map((d, f) => ({ ...d, ...((override.risers && override.risers[f]) || {}) })),
    floors: def.floors.map((row, f) =>
      row.map((d, s) => ({ ...d, ...((override.floors && override.floors[f] && override.floors[f][s]) || {}) }))
    ),
    branches: def.branches.map((d, i) => ({ ...d, ...((override.branches && override.branches[i]) || {}) })),
  };
};

/** Conductance from quadratic resistance: V̇ = c·√Δp */
const conductance = (r) => (r > 0 ? 1 / Math.sqrt(r) : 0);

/** Series combination of a resistance with a downstream conductance */
const seriesConductance = (r, c) => {
  if (c <= 0) return 0;
  return 1 / Math.sqrt(r + 1 / (c * c));
};

// #endregion

// #region NETWORK SOLVER
// ============================================================================

/** Effective valve Kv from preset position and TRV opening. */
export const effectiveKv = (presetIndex, trvOpening) => {
  const presetKv = PRESET_POSITIONS[clamp(Math.round(presetIndex), 0, PRESET_POSITIONS.length - 1)].kv;
  return presetKv * clamp(trvOpening, 0, 1);
};

/** Total quadratic resistance of one room branch (valve + radiator + its branch pipe). */
const branchResistance = (room, trvOverride, branchPipe) => {
  const trv = trvOverride !== undefined ? trvOverride : room.trv;
  const kvEff = effectiveKv(room.presetIndex, trv);
  if (kvEff < 1e-4) return Infinity;
  return (
    kvResistance(kvEff) +
    kvResistance(RADIATOR.BODY_KV) +
    pipeResistance(branchPipe.length, branchPipe.diameter)
  );
};

/**
 * Solves the complete hydraulic network.
 *
 * Topology: pump → base pipe → riser foot. The riser has a tee per floor;
 * each floor line has a tee per column; each tee feeds one room branch.
 *
 * @param {Array<Object>} rooms - Flat room array (floor-major, 9 rooms)
 * @param {number} pumpHeadKPa - Pump head setting [kPa]
 * @param {Object} [options]
 * @param {boolean} [options.forceTrvOpen] - Evaluate with all TRVs fully open
 *   (commissioning condition used for balance verification)
 * @param {Object} [options.pipes] - Adjustable pipe geometry (per-tier diameter
 *   and length); merged over the PIPES catalogue defaults.
 * @returns {Object} { roomFlows[id-1] m³/s, valveDp[], branchDp[], authority[],
 *   velocity[], totalFlow m³/s, pumpDp Pa, pumpPower W, floorFlows[] }
 */
export const solveNetwork = (rooms, pumpHeadKPa, options = {}) => {
  const { forceTrvOpen = false } = options;
  const pipes = resolvePipes(options.pipes);
  const nF = BUILDING.FLOORS;
  const nC = BUILDING.COLUMNS;

  const roomAt = (f, c) => rooms[f * nC + c];
  const rBranch = [];
  for (let f = 0; f < nF; f++) {
    for (let c = 0; c < nC; c++) {
      rBranch.push(branchResistance(roomAt(f, c), forceTrvOpen ? 1 : undefined, pipes.branches[f * nC + c]));
    }
  }

  // Per-segment resistances: rFloor[f][s] = column s→s+1 run; rRiser[f] = segment
  // leading up to floor f's tee (rRiser[0] = foot→floor0).
  const rFloor = pipes.floors.map((row) => row.map((s) => pipeResistance(s.length, s.diameter)));
  const rRiser = pipes.risers.map((s) => pipeResistance(s.length, s.diameter));
  const rBase = pipeResistance(pipes.base.length, pipes.base.diameter);

  // --- Backward reduction: per-floor line (from far column to the riser tee)
  // cFloorNode[f][c] = conductance seen at the tee of column c (incl. all columns ≥ c)
  const cFloorNode = [];
  const cFloor = []; // conductance of whole floor line at the riser tee
  for (let f = 0; f < nF; f++) {
    const nodes = new Array(nC).fill(0);
    let cAcc = 0;
    for (let c = nC - 1; c >= 0; c--) {
      const cB = isFinite(rBranch[f * nC + c]) ? conductance(rBranch[f * nC + c]) : 0;
      // segment connecting column c to its downstream (cols > c) is rFloor[f][c]
      const cDownstream = c < nC - 1 ? seriesConductance(rFloor[f][c], cAcc) : 0;
      cAcc = cB + cDownstream;
      nodes[c] = cAcc;
    }
    cFloorNode.push(nodes);
    cFloor.push(nodes[0]);
  }

  // --- Backward reduction: riser (from top floor down to the riser foot)
  // cRiserNode[f] = conductance seen at the riser tee of floor f (incl. floors ≥ f)
  const cRiserNode = new Array(nF).fill(0);
  let cAcc = 0;
  for (let f = nF - 1; f >= 0; f--) {
    // segment from floor f up to floor f+1 is rRiser[f+1]
    const cAbove = f < nF - 1 ? seriesConductance(rRiser[f + 1], cAcc) : 0;
    cAcc = cFloor[f] + cAbove;
    cRiserNode[f] = cAcc;
  }
  // Ground-floor tee sits one riser segment (rRiser[0]) above the riser foot
  const cNet = seriesConductance(rRiser[0], cRiserNode[0]);

  // --- Pump curve intersection (closed form for quadratic system curve)
  // Δp_pump = Δp0·(1 − (Q/Qmax)²) ;  Δp_system = Q²·(1/c² + r_base)
  const dp0 = pumpHeadKPa * 1000; // Pa
  const qMax = (PUMP.MAX_FLOW_AT_MAX_HEAD / 3600) * Math.sqrt(pumpHeadKPa / PUMP.MAX_HEAD_KPA);
  let totalFlow = 0;
  if (cNet > 0 && qMax > 0) {
    const denom = dp0 / (qMax * qMax) + 1 / (cNet * cNet) + rBase;
    totalFlow = Math.sqrt(dp0 / denom);
  }
  const pumpDp = dp0 * (1 - (qMax > 0 ? (totalFlow / qMax) ** 2 : 0));

  // --- Forward propagation
  const roomFlows = new Array(nF * nC).fill(0);
  const branchDp = new Array(nF * nC).fill(0);
  const valveDp = new Array(nF * nC).fill(0);
  const authority = new Array(nF * nC).fill(0);
  const velocity = new Array(nF * nC).fill(0);
  const floorFlows = new Array(nF).fill(0);
  const riserFlows = new Array(nF).fill(0); // flow in the riser segment below floor f

  // Pressure at the riser foot (after the base pipe)
  let dpNode = Math.max(pumpDp - rBase * totalFlow * totalFlow, 0);
  let qUp = totalFlow;
  for (let f = 0; f < nF; f++) {
    riserFlows[f] = qUp;
    // riser segment from below up to this floor's tee (rRiser[f])
    dpNode = Math.max(dpNode - rRiser[f] * qUp * qUp, 0);
    // split: this floor vs continue upwards (segment f→f+1 is rRiser[f+1])
    const cUpwards = f < nF - 1 ? seriesConductance(rRiser[f + 1], cRiserNode[f + 1]) : 0;
    const sqrtDp = Math.sqrt(dpNode);
    const qFloor = cFloor[f] * sqrtDp;
    floorFlows[f] = qFloor;
    const qContinue = cUpwards * sqrtDp;

    // walk along the floor line
    let dpCol = dpNode;
    let qLine = qFloor;
    for (let c = 0; c < nC; c++) {
      if (c > 0) {
        // segment from column c-1 to c is rFloor[f][c-1]
        dpCol = Math.max(dpCol - rFloor[f][c - 1] * qLine * qLine, 0);
      }
      const idx = f * nC + c;
      const rB = rBranch[idx];
      const cB = isFinite(rB) ? conductance(rB) : 0;
      const q = cB * Math.sqrt(dpCol);
      roomFlows[idx] = q;
      branchDp[idx] = dpCol;
      const trv = forceTrvOpen ? 1 : rooms[idx].trv;
      const kvEff = effectiveKv(rooms[idx].presetIndex, trv);
      valveDp[idx] = kvEff >= 1e-4 ? kvResistance(kvEff) * q * q : dpCol;
      authority[idx] = dpCol > 0 ? valveDp[idx] / dpCol : 0;
      const area = Math.PI * pipes.branches[idx].diameter ** 2 / 4;
      velocity[idx] = q / area;
      qLine = Math.max(qLine - q, 0);
    }
    qUp = qContinue;
  }

  const pumpPower = totalFlow > 0 ? (totalFlow * pumpDp) / PUMP.EFFICIENCY : 0;

  return { roomFlows, branchDp, valveDp, authority, velocity, totalFlow, pumpDp, pumpPower, floorFlows, riserFlows };
};

/**
 * Per-segment hydraulic report for the pipe editor: flow, velocity and pressure
 * drop carried by every pipe segment under the current operating point.
 *
 * @returns {{base, risers:[], floors:[][], branches:[], network}} each leaf
 *   `{ flowLph, velocity (m/s), dpKpa }`
 */
export const pipeSegmentReport = (rooms, pumpHeadKPa, pipesOverride) => {
  const pipes = resolvePipes(pipesOverride);
  const net = solveNetwork(rooms, pumpHeadKPa, { pipes });
  const nC = BUILDING.COLUMNS;

  const mk = (q, seg) => ({
    flowLph: toLitersPerHour(q),
    velocity: q / (Math.PI * seg.diameter * seg.diameter / 4),
    dpKpa: (pipeResistance(seg.length, seg.diameter) * q * q) / 1000,
  });

  return {
    base: mk(net.totalFlow, pipes.base),
    risers: pipes.risers.map((seg, f) => mk(net.riserFlows[f], seg)),
    floors: pipes.floors.map((row, f) =>
      row.map((seg, s) => {
        let q = 0;
        for (let c = s + 1; c < nC; c++) q += net.roomFlows[f * nC + c];
        return mk(q, seg);
      })
    ),
    branches: pipes.branches.map((seg, idx) => mk(net.roomFlows[idx], seg)),
    network: net,
  };
};

// #endregion

// #region DESIGN HEAT LOAD & DESIGN FLOW (Verfahren B foundation)
// ============================================================================

/** Setpoint of a neighbour room or null if the surface faces outside. */
const neighbourSetpoint = (rooms, f, c) => {
  if (f < 0 || f >= BUILDING.FLOORS || c < 0 || c >= BUILDING.COLUMNS) return null;
  return rooms[f * BUILDING.COLUMNS + c].Tset;
};

/**
 * DIN EN 12831-style design heat load of one room at the design outdoor
 * temperature: transmission (exterior + to differently heated neighbours)
 * plus ventilation. Heat gains from warmer neighbours are NOT credited
 * (standard practice: loads to colder rooms count, gains are ignored).
 *
 * @returns {number} Design heat load [W]
 */
export const designHeatLoad = (rooms, room) => {
  const tOut = OUTDOOR.DESIGN;
  const { Tset, Uout, floor, col } = room;
  let load = 0;

  // Front & back walls: always exterior
  load += 2 * Uout * AREAS.WALL_FRONT_BACK * (Tset - tOut);

  // Side walls (east/west)
  for (const dc of [-1, 1]) {
    const tn = neighbourSetpoint(rooms, floor, col + dc);
    if (tn === null) {
      load += Uout * AREAS.WALL_SIDE * (Tset - tOut);
    } else if (tn < Tset) {
      load += BUILDING.U_INTERNAL * AREAS.WALL_SIDE * (Tset - tn);
    }
  }

  // Ceiling
  const tAbove = neighbourSetpoint(rooms, floor + 1, col);
  if (tAbove === null) {
    load += Uout * BUILDING.ROOF_U_FACTOR * AREAS.FLOOR_CEILING * (Tset - tOut);
  } else if (tAbove < Tset) {
    load += BUILDING.U_INTERNAL * AREAS.FLOOR_CEILING * (Tset - tAbove);
  }

  // Floor
  const tBelow = neighbourSetpoint(rooms, floor - 1, col);
  if (tBelow === null) {
    const tGround = Math.max(tOut, BUILDING.GROUND_MIN_TEMP);
    load += Uout * BUILDING.GROUND_U_FACTOR * AREAS.FLOOR_CEILING * (Tset - tGround);
  } else if (tBelow < Tset) {
    load += BUILDING.U_INTERNAL * AREAS.FLOOR_CEILING * (Tset - tBelow);
  }

  // Ventilation
  load += AIR.VOLUMETRIC_HEAT_CAPACITY * ROOM_VOLUME * 0.5 * (Tset - tOut);

  return Math.max(load, 0);
};

/**
 * Design volume flow from the design heat load and the design spread:
 * V̇ = Q / (ρ·cp·ΔT)  [m³/s]
 */
export const designFlow = (loadW) =>
  loadW / (WATER.DENSITY * WATER.SPECIFIC_HEAT * HEAT_PUMP.DESIGN_SPREAD);

/**
 * Recommended radiator nominal size (ΔT50) so the radiator just covers the
 * design load at design supply/return with ~15% margin, rounded to catalogue.
 */
export const recommendedRadiatorSize = (loadW, Tset) => {
  const meanDT = (HEAT_PUMP.DESIGN_SUPPLY + (HEAT_PUMP.DESIGN_SUPPLY - HEAT_PUMP.DESIGN_SPREAD)) / 2 - Tset;
  const factor = Math.pow(Math.max(meanDT, 1) / RADIATOR.NOMINAL_DT, RADIATOR.EXPONENT);
  const required = (loadW / factor) * 1.15;
  const size = RADIATOR.SIZES.find((s) => s >= required);
  return size || RADIATOR.SIZES[RADIATOR.SIZES.length - 1];
};

/**
 * Annotates all rooms with design load and design flow. By default also
 * auto-sizes the radiators; pass resizeRadiators=false to keep the installed
 * sizes (e.g. after a renovation changed the heat load).
 */
export const applyDesignCalculation = (rooms, { resizeRadiators = true } = {}) =>
  rooms.map((room) => {
    const load = designHeatLoad(rooms, room);
    return {
      ...room,
      designLoad: load,
      designFlow: designFlow(load),
      radiatorNominal: resizeRadiators
        ? recommendedRadiatorSize(load, room.Tset)
        : room.radiatorNominal,
    };
  });

// #endregion

// #region RADIATOR (EN 442)
// ============================================================================

/**
 * Radiator output and return temperature from an energy-balance iteration:
 * ṁ·cp·(Tsup − Tret) = K·ΔT_m^n with ΔT_m = (Tsup+Tret)/2 − Troom
 *
 * @returns {Object} { qrad W, Treturn °C }
 */
export const radiatorPerformance = ({ tSupply, tRoom, flow, nominal }) => {
  const massFlow = flow * WATER.DENSITY;
  if (massFlow < 1e-5) return { qrad: 0, Treturn: tRoom };

  const K = nominal / Math.pow(RADIATOR.NOMINAL_DT, RADIATOR.EXPONENT);
  let Treturn = Math.max(tSupply - HEAT_PUMP.DESIGN_SPREAD, tRoom);

  for (let i = 0; i < RADIATOR.ITERATIONS; i++) {
    const meanDT = (tSupply + Treturn) / 2 - tRoom;
    const q = meanDT > 0 ? K * Math.pow(meanDT, RADIATOR.EXPONENT) : 0;
    const dT = q / (massFlow * WATER.SPECIFIC_HEAT);
    Treturn = Math.max(tRoom, tSupply - clamp(dT, 0, 60));
  }

  const meanDT = (tSupply + Treturn) / 2 - tRoom;
  const qrad = meanDT > 0 ? K * Math.pow(meanDT, RADIATOR.EXPONENT) : 0;
  return { qrad, Treturn };
};

// #endregion

// #region ROOM HEAT LOSSES (live, 6-sided)
// ============================================================================

/** Current temperature of a neighbour room or null if exterior. */
const neighbourTemp = (rooms, f, c) => {
  if (f < 0 || f >= BUILDING.FLOORS || c < 0 || c >= BUILDING.COLUMNS) return null;
  return rooms[f * BUILDING.COLUMNS + c].T;
};

/**
 * Instantaneous heat losses of one room (transmission to outside, exchange
 * with neighbours, ventilation). Negative contributions = gains.
 */
export const roomLosses = (rooms, room, tOutdoor, airChangeRate) => {
  const { T, Uout, floor, col } = room;

  let toOutside = 2 * Uout * AREAS.WALL_FRONT_BACK * (T - tOutdoor);
  let interRoom = 0;

  for (const dc of [-1, 1]) {
    const tn = neighbourTemp(rooms, floor, col + dc);
    if (tn === null) toOutside += Uout * AREAS.WALL_SIDE * (T - tOutdoor);
    else interRoom += BUILDING.U_INTERNAL * AREAS.WALL_SIDE * (T - tn);
  }

  const tAbove = neighbourTemp(rooms, floor + 1, col);
  if (tAbove === null) toOutside += Uout * BUILDING.ROOF_U_FACTOR * AREAS.FLOOR_CEILING * (T - tOutdoor);
  else interRoom += BUILDING.U_INTERNAL * AREAS.FLOOR_CEILING * (T - tAbove);

  const tBelow = neighbourTemp(rooms, floor - 1, col);
  if (tBelow === null) {
    const tGround = Math.max(tOutdoor, BUILDING.GROUND_MIN_TEMP);
    toOutside += Uout * BUILDING.GROUND_U_FACTOR * AREAS.FLOOR_CEILING * (T - tGround);
  } else {
    interRoom += BUILDING.U_INTERNAL * AREAS.FLOOR_CEILING * (T - tBelow);
  }

  const ventilation = AIR.VOLUMETRIC_HEAT_CAPACITY * ROOM_VOLUME * airChangeRate * (T - tOutdoor);

  return { toOutside, interRoom, ventilation, total: toOutside + interRoom + ventilation };
};

// #endregion

// #region HEAT PUMP
// ============================================================================

/** Carnot-with-efficiency COP. */
export const heatPumpCOP = (tSupply, tOutdoor) => {
  const tCond = tSupply + HEAT_PUMP.CONDENSER_APPROACH + 273.15;
  const tEvap = tOutdoor - HEAT_PUMP.EVAPORATOR_APPROACH + 273.15;
  if (tCond <= tEvap) return 8;
  const cop = HEAT_PUMP.COMPRESSOR_EFFICIENCY * (tCond / (tCond - tEvap));
  return clamp(cop, 1.2, 8);
};

/** Weather-compensated supply temperature: Tsup = 20 + offset + slope·(20 − Tout). */
export const heatingCurveSupply = (tOutdoor, slope, offset) =>
  clamp(20 + offset + slope * (20 - tOutdoor), HEAT_PUMP.MIN_SUPPLY, HEAT_PUMP.MAX_SUPPLY);

/** Active supply temperature for the current parameter set. */
export const currentSupplyTemp = (params) =>
  params.supplyMode === 'curve'
    ? heatingCurveSupply(params.tOutdoor, params.curveSlope, params.curveOffset)
    : params.tSupplyFixed;

/** Flow-weighted mixed return temperature. */
export const mixedReturnTemp = (rooms, tSupply) => {
  let sumQT = 0;
  let sumQ = 0;
  rooms.forEach((r) => {
    sumQT += r.flow * r.Treturn;
    sumQ += r.flow;
  });
  return sumQ > 1e-9 ? sumQT / sumQ : tSupply;
};

// #endregion

// #region TRV CONTROL
// ============================================================================

/**
 * Proportional TRV: fully open 1 K below setpoint, fully closed 1 K above
 * (2 K P-band centred on the setpoint, like a real thermostatic head).
 */
export const trvOpening = (tRoom, tSet) =>
  clamp(0.5 + (tSet - tRoom) / TRV_P_BAND, 0, 1);

// #endregion

// #region SIMULATION STEP
// ============================================================================

/**
 * Advances all room temperatures by dt seconds and returns the updated rooms
 * plus aggregate results. The hydraulic state (flows) must already be solved
 * for the current TRV openings and is passed in via `network`.
 *
 * @returns {Object} { rooms, totals: { qrad, losses } }
 */
export const thermalStep = ({ rooms, network, tSupply, tOutdoor, airChangeRate, dt, balancingMode }) => {
  let totalQrad = 0;
  let totalLosses = 0;

  const updated = rooms.map((room, idx) => {
    const flow = network.roomFlows[idx];
    const { qrad, Treturn } = radiatorPerformance({
      tSupply,
      tRoom: room.T,
      flow,
      nominal: room.radiatorNominal,
    });
    const losses = roomLosses(rooms, room, tOutdoor, airChangeRate);

    const dT = ((qrad - losses.total) * dt) / ROOM_THERMAL_CAPACITANCE;
    const T = room.T + dT;
    const trv = balancingMode ? 1 : trvOpening(T, room.Tset);

    totalQrad += qrad;
    totalLosses += losses.total;

    return {
      ...room,
      T,
      trv,
      flow,
      qrad,
      Treturn,
      losses,
      authority: network.authority[idx],
      velocity: network.velocity[idx],
    };
  });

  return { rooms: updated, totals: { qrad: totalQrad, losses: totalLosses } };
};

// #endregion

// #region SCORING & EVALUATION
// ============================================================================

/**
 * Flow deviations vs design flow under commissioning conditions (TRVs open).
 * @returns {Array<number>} deviation fraction per room, e.g. +0.25 = 25% overflow
 */
export const balanceDeviations = (rooms, pumpHeadKPa, pipes) => {
  const net = solveNetwork(rooms, pumpHeadKPa, { forceTrvOpen: true, pipes });
  return rooms.map((room, idx) =>
    room.designFlow > 1e-9 ? (net.roomFlows[idx] - room.designFlow) / room.designFlow : 0
  );
};

/** Balance score 0–100 from commissioning-condition deviations. */
export const balanceScore = (deviations) => {
  if (!deviations.length) return 0;
  const meanAbs = deviations.reduce((s, d) => s + Math.abs(d), 0) / deviations.length;
  return clamp(Math.round(100 * (1 - meanAbs / 0.5)), 0, 100);
};

/** Comfort score 0–100 from current temperature errors. */
export const comfortScore = (rooms) => {
  if (!rooms.length) return 0;
  const per = rooms.map((r) => clamp(1 - Math.abs(r.T - r.Tset) / 3, 0, 1));
  return Math.round((per.reduce((s, v) => s + v, 0) / rooms.length) * 100);
};

/**
 * Efficiency score 0–100 combining COP (vs the achievable range at the current
 * outdoor temperature) and pump power.
 */
export const efficiencyScore = (cop, tOutdoor, pumpPower) => {
  const copBest = heatPumpCOP(HEAT_PUMP.MIN_SUPPLY + 5, tOutdoor);
  const copWorst = heatPumpCOP(HEAT_PUMP.MAX_SUPPLY, tOutdoor);
  const copPart = clamp((cop - copWorst) / Math.max(copBest - copWorst, 0.1), 0, 1);
  const pumpPart = clamp(1 - pumpPower / 120, 0, 1);
  return Math.round((0.8 * copPart + 0.2 * pumpPart) * 100);
};

// #endregion

// #region AUTO-BALANCING (reference solution)
// ============================================================================

/**
 * Computes preset positions that bring every room close to its design flow
 * (used for mission setups and "show solution"). Iterative: nudge each Kv by
 * the flow error and snap to the nearest catalogue position.
 *
 * @returns {Array<number>} presetIndex per room
 */
export const autoBalancePresets = (rooms, pumpHeadKPa, pipes) => {
  let working = rooms.map((r) => ({ ...r }));

  for (let iter = 0; iter < 40; iter++) {
    const net = solveNetwork(working, pumpHeadKPa, { forceTrvOpen: true, pipes });
    working = working.map((room, idx) => {
      const q = net.roomFlows[idx];
      if (room.designFlow < 1e-9 || q < 1e-9) return room;
      const currentKv = PRESET_POSITIONS[room.presetIndex].kv;
      const targetKv = clamp(
        currentKv * Math.pow(room.designFlow / q, 0.7),
        PRESET_POSITIONS[0].kv,
        PRESET_POSITIONS[PRESET_POSITIONS.length - 1].kv
      );
      // nearest catalogue position
      let best = 0;
      let bestErr = Infinity;
      PRESET_POSITIONS.forEach((p, i) => {
        const err = Math.abs(Math.log(p.kv / targetKv));
        if (err < bestErr) {
          bestErr = err;
          best = i;
        }
      });
      return { ...room, presetIndex: best };
    });
  }

  // Local refinement: coordinate descent on the worst absolute deviation.
  const maxAbsDev = (rs) => {
    const net = solveNetwork(rs, pumpHeadKPa, { forceTrvOpen: true, pipes });
    return Math.max(
      ...rs.map((room, idx) =>
        room.designFlow > 1e-9 ? Math.abs((net.roomFlows[idx] - room.designFlow) / room.designFlow) : 0
      )
    );
  };
  for (let sweep = 0; sweep < 3; sweep++) {
    for (let i = 0; i < working.length; i++) {
      let bestIdx = working[i].presetIndex;
      let bestScore = maxAbsDev(working);
      for (const delta of [-1, 1]) {
        const tryIdx = working[i].presetIndex + delta;
        if (tryIdx < 0 || tryIdx >= PRESET_POSITIONS.length) continue;
        const candidate = working.map((r, j) => (j === i ? { ...r, presetIndex: tryIdx } : r));
        const score = maxAbsDev(candidate);
        if (score < bestScore) {
          bestScore = score;
          bestIdx = tryIdx;
        }
      }
      working = working.map((r, j) => (j === i ? { ...r, presetIndex: bestIdx } : r));
    }
  }

  return working.map((r) => r.presetIndex);
};

/**
 * Convenience: checks whether all rooms are within tolerance of design flow
 * under commissioning conditions.
 */
export const isBalanced = (rooms, pumpHeadKPa, tolerance = SCORING.BALANCE_TOLERANCE, pipes) =>
  balanceDeviations(rooms, pumpHeadKPa, pipes).every((d) => Math.abs(d) <= tolerance);

// #endregion

// #region SYSTEM OPTIMIZATION (supply temperature + presets)
// ============================================================================

/**
 * Steady heat demand of every room held at its setpoint, at the current outdoor
 * temperature — i.e. the power each radiator must deliver to hold the setpoint.
 * @returns {Array<number>} demand per room [W]
 */
export const steadyHeatDemand = (rooms, params) => {
  const atSet = rooms.map((r) => ({ ...r, T: r.Tset }));
  return atSet.map((r) =>
    Math.max(roomLosses(atSet, r, params.tOutdoor, params.airChangeRate).total, 0)
  );
};

/**
 * Mean radiator water temperature required to deliver a given heat output from a
 * radiator of the given nominal size (EN 442 inverse): from Q = K·ΔTm^n with
 * K = nominal/ΔT50^n, ΔTm = ΔT50·(Q/nominal)^(1/n), Tavg = Troom + ΔTm.
 */
export const requiredMeanRadiatorTemp = (demandW, nominal, tRoom) => {
  if (demandW <= 0) return tRoom;
  return tRoom + RADIATOR.NOMINAL_DT * Math.pow(demandW / nominal, 1 / RADIATOR.EXPONENT);
};

/**
 * Optimises supply temperature and per-room valve presets so every room meets
 * its heat demand. The weakest room (highest required supply) sets the system
 * supply temperature; flows are sized from each demand and a design spread, and
 * presets are derived with the existing auto-balancer. The pump head is left
 * untouched; infeasible rooms (radiator too small / pump-limited) are reported.
 *
 * @returns {Object} analysis + applied plan + before/after metrics + steps[]
 */
export const runOptimization = (rooms, params) => {
  const spread = HEAT_PUMP.DESIGN_SPREAD;
  const tOut = params.tOutdoor;
  const demand = steadyHeatDemand(rooms, params);
  const netBefore = solveNetwork(rooms, params.pumpHead, { pipes: params.pipes });

  const perRoom = rooms.map((room, i) => {
    const d = demand[i];
    const tAvgReq = requiredMeanRadiatorTemp(d, room.radiatorNominal, room.Tset);
    return {
      idx: i,
      id: room.id,
      name: room.name,
      demand: d,
      eta: d > 1 ? room.qrad / d : 1, // radiator coverage = output ÷ demand
      tAvgReq,
      tSupReq: tAvgReq + spread / 2,
      designFlow: room.designFlow, // m³/s — the balancing target
      presetBefore: room.presetIndex,
      flowBefore: netBefore.roomFlows[i],
    };
  });

  // weakest room = lowest η (worst radiator coverage)
  let weakestIdx = 0;
  perRoom.forEach((p) => { if (p.eta < perRoom[weakestIdx].eta) weakestIdx = p.idx; });

  // supply temperature is set by the most-demanding room (highest required supply)
  const rawSupply = Math.max(...perRoom.map((p) => p.tSupReq));
  const tSupply = clamp(rawSupply, HEAT_PUMP.MIN_SUPPLY, HEAT_PUMP.MAX_SUPPLY);
  const supplyCapped = rawSupply > HEAT_PUMP.MAX_SUPPLY + 0.05;
  const undersized = perRoom.filter((p) => p.tSupReq > tSupply + 0.05).map((p) => p.idx);

  // balance every branch to its design flow (commissioning conditions); pump head fixed
  const presets = autoBalancePresets(rooms, params.pumpHead, params.pipes);
  const balancedRooms = rooms.map((room, i) => ({ ...room, presetIndex: presets[i] }));

  const commissioningAfter = solveNetwork(balancedRooms, params.pumpHead, { forceTrvOpen: true, pipes: params.pipes });
  const operatingAfter = solveNetwork(balancedRooms, params.pumpHead, { pipes: params.pipes });
  const pumpLimited = perRoom
    .filter((p) => p.designFlow > 1e-9 && commissioningAfter.roomFlows[p.idx] < p.designFlow * 0.9)
    .map((p) => p.idx);

  perRoom.forEach((p) => {
    p.presetAfter = presets[p.idx];
    p.flowAfter = commissioningAfter.roomFlows[p.idx];
  });

  const copBefore = heatPumpCOP(currentSupplyTemp(params), tOut);
  const copAfter = heatPumpCOP(tSupply, tOut);
  const before = {
    tSupply: currentSupplyTemp(params),
    cop: copBefore,
    balance: balanceScore(balanceDeviations(rooms, params.pumpHead, params.pipes)),
    efficiency: efficiencyScore(copBefore, tOut, netBefore.pumpPower),
  };
  const after = {
    tSupply,
    cop: copAfter,
    balance: balanceScore(balanceDeviations(balancedRooms, params.pumpHead, params.pipes)),
    efficiency: efficiencyScore(copAfter, tOut, operatingAfter.pumpPower),
  };

  const steps = [
    `Computed each room's steady heat demand at its setpoint and the current outdoor temperature (${tOut.toFixed(0)} °C), and its coverage η = radiator output ÷ demand.`,
    `Identified the weakest room: ${rooms[weakestIdx].name} (η = ${perRoom[weakestIdx].eta.toFixed(2)}) — the lowest radiator coverage.`,
    `Required mean radiator temperature to cover the most-demanding room ≈ ${rawSupply > 0 ? (rawSupply - spread / 2).toFixed(1) : '—'} °C → supply ${rawSupply.toFixed(1)} °C${supplyCapped ? ` (capped at ${HEAT_PUMP.MAX_SUPPLY} °C)` : ''}.`,
    `Set the supply temperature to ${tSupply.toFixed(1)} °C (fixed) — the lowest value that still covers the demand, for the best COP.`,
    `Balanced every valve preset to its design flow (V̇ = Q̇_design / (ρ·c_p·ΔT)) within the current pump head (${params.pumpHead.toFixed(0)} kPa).`,
  ];
  if (undersized.length) {
    steps.push(`⚠ ${undersized.map((i) => rooms[i].name).join(', ')}: radiator too small to meet the demand even at ${HEAT_PUMP.MAX_SUPPLY} °C — enlarge the radiator.`);
  }
  if (pumpLimited.length) {
    steps.push(`⚠ ${pumpLimited.map((i) => rooms[i].name).join(', ')}: the pump head can't push the required flow — raise the pump head or widen its pipes.`);
  }
  steps.push(`Result: balance ${before.balance} → ${after.balance}, efficiency ${before.efficiency} → ${after.efficiency}. Comfort will improve as the rooms settle.`);

  return { spread, perRoom, weakestIdx, tSupply, rawSupply, supplyCapped, undersized, pumpLimited, presets, before, after, steps };
};

// #endregion
